import { isoCountryName } from "@libs/utils/isoCountries";
import { vendorDeliveryFeeMinor } from "./schemas/vendor-checkout";
import type { FanEstimateRate } from "@libs/money";
import { headlinePriceLabel, inPounds, storeMoneyMinor } from "./store-money";
import type { VendorDeliveryFeeTier } from "./schemas/vendor-checkout";
import {
  linkPageMerchAccessSchema,
  type LinkPageMerchAccess,
} from "./schemas/link-page-access";
import type { AccessLineupRow } from "./schemas/access-lineup";

/** "£15.00", or "From £15.00" when the sizes are priced differently
 *  (`merchHeadline`'s `price`), on Link in Bio and in the app alike; in its
 *  store's currency where that is not pounds ("US$45.00", `storeMoney`),
 *  with the estimate in the fan's own currency where the journey has one,
 *  and "+ local tax" where its seller adds tax at checkout
 *  (`headlinePriceLabel`). */
export function merchPriceLabel(
  price: { amount: number; currency: string; from?: boolean } | null,
  approx?: FanEstimateRate | null,
  taxAdded?: boolean | null,
) {
  return price ? headlinePriceLabel(price, approx, taxAdded) : null;
}

/** Names said with "the" ("delivery to the United States"). */
const NAMED_WITH_THE = /^(United |Central African |Dominican |Netherlands$|Bahamas$|Philippines$|Gambia$|Maldives$|Comoros$)|Islands$/;

/** The delivery a fan's order is priced for, mid-sentence: "UK delivery"
 *  for GB, "delivery to Switzerland", or "delivery" where the destination is
 *  unknown; never inferred from a currency. Names come from the shared
 *  country list, not `Intl.DisplayNames`, which the app's Hermes lacks. */
function deliveryWords(country?: string | null): string {
  const code = country?.trim().toUpperCase();
  if (code === "GB") return "UK delivery";
  const name = code ? isoCountryName(code) : undefined;
  if (!name) return "delivery";
  return `delivery to ${NAMED_WITH_THE.test(name) ? "the " : ""}${name}`;
}

/** The delivery line's label for its destination (`deliveryWords`):
 *  "UK delivery", "Delivery to Switzerland" or "Delivery", and "Free UK
 *  delivery", "Free delivery to Switzerland" or "Free delivery" when free. */
export function merchDeliveryLabel(country?: string | null, free = false): string {
  const words = deliveryWords(country);
  return free ? `Free ${words}` : words[0]!.toUpperCase() + words.slice(1);
}

/** "Free delivery", "+ £7.35 delivery", or "+ delivery" where the store
 *  quotes its own for each basket (a null `deliveryMinor`): what a merch
 *  product's page says beside its price (`merchHeadline`'s `deliveryMinor`),
 *  on Link in Bio and in the app alike. A page that knows the destination
 *  names it (`deliveryWords`): "+ £7.35 UK delivery". */
export function merchDeliveryNote(
  deliveryMinor: number | null,
  currency: string,
  country?: string | null,
) {
  const words = deliveryWords(country);
  if (deliveryMinor === null) return `+ ${words}`;
  return deliveryMinor > 0
    ? `+ ${storeMoneyMinor(deliveryMinor, currency)} ${words}`
    : merchDeliveryLabel(country, true);
}

/** `merchDeliveryNote` for a product priced in `currency`: none where its
 *  store sells in another currency than pounds, which has no UK delivery to
 *  say (1-tap cannot buy it for UK delivery). */
export function merchDeliveryNoteIn(
  deliveryMinor: number | null,
  currency: string,
  country?: string | null,
): string | null {
  return inPounds(currency) ? merchDeliveryNote(deliveryMinor, currency, country) : null;
}

/** What a digital download's page says in place of its delivery (its
 *  purchase's delivery is `emailed`), on Link in Bio and in the app alike:
 *  that it is one (`label`, beside its price), and how the buyer gets it
 *  (`note`). Its purchase sheet's order line says `label` too, where a
 *  posted product's says its delivery. */
export const MERCH_DOWNLOAD_WORDS = {
  label: "Digital download",
  note: "The store emails you a link to download it. Nothing is posted.",
} as const;

/** A delivery line: "UK delivery" and its fee ("£5.54"), or "Free UK
 *  delivery" and none (`merchDeliveryLabel`). */
export type MerchDelivery = { label: string; amount: string | null };

/**
 * The purchase sheet's delivery line for the sizes chosen, part of the
 * total its button says: "UK delivery" and its fee, or "Free UK delivery",
 * named for the sheet's delivery country (`merchDeliveryLabel`).
 * The fee is the checkout's own (`vendorDeliveryFeeMinor`, which
 * `vendorOrderFeeMinor` adds) for the order's subtotal, booking fees
 * included, so it moves along the ladder as the quantity does. Null with
 * nothing chosen. On Link in Bio and in the app alike.
 */
export function merchDeliveryLine(
  journey: {
    currency: string;
    deliveryFeeTiers?: readonly VendorDeliveryFeeTier[];
  },
  subtotalMinor: number,
  country?: string | null,
): MerchDelivery | null {
  if (subtotalMinor <= 0) return null;
  const feeMinor = vendorDeliveryFeeMinor(
    journey.deliveryFeeTiers,
    subtotalMinor,
  );
  return feeMinor > 0
    ? {
        label: merchDeliveryLabel(country),
        amount: storeMoneyMinor(feeMinor, journey.currency),
      }
    : { label: merchDeliveryLabel(country, true), amount: null };
}

/** Everything the merch page needs, already loaded. Pure: no I/O, no clock. */
export type LinkPageMerchInput = {
  artist: { slug: string; id: string; name: string; imageUrl: string | null };
  lineup?: AccessLineupRow[];
  access: {
    id: string;
    title: string;
    description: string | null;
    /** Already resolved for display. */
    imageUrl: string | null;
    externalUrl: string | null;
  };
  /** What the product's page says before a size is chosen, as the server
   *  prices its 1-tap listing (`merchHeadline`, `@libs/buy-on-behalf`
   *  `seller-data`); null with no listing on sale. */
  headline: { price: { amount: number; currency: string; from: boolean } } | null;
  /** Buy follows `externalUrl` rather than 1-tap: the artist's own store while
   *  its workspace's 1-tap is off there. */
  external?: boolean;
};

/** Turn stored rows into the wire shape the Link in Bio merch page renders:
 *  sold here while its listing has a size on sale, unavailable otherwise. */
export function resolveLinkPageMerchAccess(
  input: LinkPageMerchInput,
): LinkPageMerchAccess {
  const { access, artist, headline } = input;
  return linkPageMerchAccessSchema.parse({
    slug: artist.slug,
    artist: { id: artist.id, name: artist.name, imageUrl: artist.imageUrl },
    access: {
      type: "merch",
      id: access.id,
      title: access.title,
      lineup: input.lineup?.length ? input.lineup : undefined,
      description: access.description,
      imageUrl: access.imageUrl ?? artist.imageUrl,
      externalUrl: access.externalUrl,
      checkoutMode: !headline
        ? "unavailable"
        : input.external
          ? "external"
          : "paid",
      price: headline?.price ?? null,
    },
    // The read's purchase is compiled by the server for its reader and
    // set on the page it sends (LIS-1173): none from stored rows alone.
    purchase: null,
  });
}
